import { layers } from './popupLayers'
import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type SyntheticEvent,
} from 'react'
import clsx from 'clsx'
import Popover from '@features/shared/components/Popover'
import { HOVER_INTENT_DELAY_MS, HOVER_MENU_CLOSE_DELAY_MS } from '@/styling/tokens.interaction'
import { isQuietFocus } from '../utils/quietFocus'

interface TooltipProps {
  children: ReactNode
  content: ReactNode
  align?: 'left' | 'right'
  placement?: 'default' | 'right-start' | 'right'
  className?: string
  contentClassName?: string
  sideOffsetPx?: number
  /** Also show when a composite control virtually focuses its child. */
  forceOpen?: boolean
  fullWidth?: boolean
  /**
   * A click (or Enter or Space) pins the card open until clicked again, a click
   * elsewhere, or Escape. The click goes no further, so the trigger can sit
   * inside something else clickable. For triggers that only explain, such as
   * an info icon.
   */
  clickToggle?: boolean
  /** Make the trigger focusable, for content that can't be: a disabled button or plain text. */
  focusable?: boolean
  /** `card` is fixed-width for longer help; `label` sizes to its text. */
  variant?: 'card' | 'label'
}

/** No content (null, false or '') shows no card, so a trigger can keep its wrapper. */
function hasTooltipContent(content: ReactNode) {
  return content !== null && content !== undefined && content !== false && content !== ''
}

/**
 * Opens after the pointer rests `HOVER_INTENT_DELAY_MS` on its trigger, at once
 * on keyboard focus, and closes when either leaves (unless pinned by a click).
 * A `card` can be read where it is: the pointer can move onto it, and it stays
 * until the pointer leaves both, waiting `HOVER_MENU_CLOSE_DELAY_MS` for the
 * pointer to cross the gap. A `label` closes as soon as the pointer leaves.
 */
export function Tooltip({
  children,
  content,
  align = 'left',
  placement = 'default',
  className,
  contentClassName,
  sideOffsetPx,
  forceOpen = false,
  fullWidth = false,
  clickToggle = false,
  focusable = false,
  variant = 'card',
}: TooltipProps) {
  const [open, setOpen] = useState(false)
  const shown = (open || forceOpen) && hasTooltipContent(content)
  const tooltipId = useId()
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hoverable = variant === 'card'
  // Shown by a click: leaving or blurring doesn't hide it.
  const pinned = useRef(false)
  // Hidden by a click: hovering on doesn't bring it back until the pointer leaves.
  const dismissed = useRef(false)
  // Whether the card showed when the press began: the press focuses the
  // trigger, which shows it, before the click decides.
  const openAtPress = useRef(false)

  const cancelOpen = () => {
    if (openTimer.current !== null) clearTimeout(openTimer.current)
    openTimer.current = null
  }
  const cancelClose = () => {
    if (closeTimer.current !== null) clearTimeout(closeTimer.current)
    closeTimer.current = null
  }
  const show = () => {
    cancelOpen()
    cancelClose()
    setOpen(true)
  }
  const hide = () => {
    cancelOpen()
    cancelClose()
    if (!pinned.current) setOpen(false)
  }
  // The pointer left the trigger or the card: a shown card waits for it to
  // reach the other.
  const leave = () => {
    if (!hoverable || !open) {
      hide()
      return
    }
    cancelOpen()
    cancelClose()
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null
      if (!pinned.current) setOpen(false)
    }, HOVER_MENU_CLOSE_DELAY_MS)
  }
  const toggle = (wasOpen: boolean) => {
    cancelOpen()
    cancelClose()
    pinned.current = !wasOpen
    dismissed.current = wasOpen
    setOpen(!wasOpen)
  }
  const stop = (event: SyntheticEvent) => event.stopPropagation()
  useEffect(
    () => () => {
      cancelOpen()
      cancelClose()
    },
    []
  )

  return (
    <Popover
      open={shown}
      onOpenChange={(next) => {
        if (!next) pinned.current = false
        setOpen(next)
      }}
      align={align}
      placement={placement}
      sideOffsetPx={sideOffsetPx}
      showCaret={false}
      triggerMode="manual"
      contentId={tooltipId}
      contentRole="tooltip"
      contentHandlers={
        hoverable
          ? {
              onMouseEnter: cancelClose,
              onMouseLeave: leave,
              // Portaled, its clicks would still reach the trigger's ancestors
              // (a row, a selector), as the trigger's own don't.
              onClick: stop,
              onDoubleClick: stop,
            }
          : undefined
      }
      portalZIndex={layers.tooltip}
      wrapperClassName={clsx('inline-flex', fullWidth && 'w-full', className)}
      triggerClassName={clsx('inline-flex', fullWidth && 'w-full')}
      contentClassName={clsx(
        'border-default surface-default text-left text-xs text-subtle',
        variant === 'label' ? '!w-max max-w-72 px-2 py-1' : 'w-72 px-3 py-2',
        contentClassName
      )}
      trigger={
        <span
          aria-describedby={shown ? tooltipId : undefined}
          tabIndex={focusable ? 0 : undefined}
          className={clsx('inline-flex', fullWidth && 'w-full')}
          onMouseEnter={() => {
            cancelClose()
            if (dismissed.current || open) return
            cancelOpen()
            openTimer.current = setTimeout(() => {
              openTimer.current = null
              setOpen(true)
            }, HOVER_INTENT_DELAY_MS)
          }}
          onMouseLeave={() => {
            dismissed.current = false
            leave()
          }}
          onFocus={() => {
            // Focus a dialog moves in or gives back isn't the person's; it opens nothing.
            if (!dismissed.current && !isQuietFocus()) show()
          }}
          onBlur={hide}
          {...(clickToggle
            ? {
                onMouseDown: () => {
                  openAtPress.current = open
                },
                onClick: (event: SyntheticEvent) => {
                  stop(event)
                  toggle(openAtPress.current)
                  openAtPress.current = false
                },
                onDoubleClick: stop,
                onKeyDown: (event: KeyboardEvent) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return
                  event.preventDefault()
                  stop(event)
                  toggle(open)
                },
              }
            : {
                // A press hides the card until the pointer leaves, so clicking a
                // menu's trigger doesn't cover the menu. Capture, so a control
                // that stops its own mousedown still counts.
                onMouseDownCapture: () => {
                  dismissed.current = true
                  hide()
                },
              })}
        >
          {children}
        </span>
      }
    >
      {content}
    </Popover>
  )
}
